/**
 * IndexedDB persistence of RESUMABLE upload state (F3-F). Survives
 * reloads: reopening the upload for the same target (`resumeKey`) recovers
 * `{assetId, uploadId, uploadedParts}` and the machine resumes with list_parts.
 *
 * Minimal API (`get`/`put`/`del`) over a single object store. Degrades to a no-op
 * if IndexedDB isn't available (SSR / restricted browsers) so as not to break
 * the upload — only cross-reload resumption is lost.
 */

import type { CompletedPart, ResumeState } from "@space/shared";

const DB_NAME = "space-media-uploads";
const STORE = "resumable";
const DB_VERSION = 1;

/** Persisted record of an in-progress upload. */
export interface PersistedUpload {
  /** Stable target key (e.g. `${courseId}:${lessonId}:${size}`). */
  resumeKey: string;
  assetId: string;
  uploadId: string;
  uploadedParts: CompletedPart[];
  fileName: string;
  sizeBytes: number;
  title: string;
  updatedAt: number;
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    const idb = globalThis.indexedDB;
    if (!idb) {
      resolve(null);
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = idb.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.addEventListener("upgradeneeded", () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "resumeKey" });
      }
    });
    req.addEventListener("success", () => resolve(req.result));
    req.addEventListener("error", () => resolve(null));
  });
}

function tx(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<unknown> {
  return openDb().then(
    (db) =>
      new Promise<unknown>((resolve) => {
        if (!db) {
          resolve(null);
          return;
        }
        try {
          const store = db.transaction(STORE, mode).objectStore(STORE);
          const req = run(store);
          req.addEventListener("success", () => resolve(req.result ?? null));
          req.addEventListener("error", () => resolve(null));
        } catch {
          resolve(null);
        }
      }),
  );
}

/** Saves/updates a resumable upload's state. */
export async function savePersistedUpload(record: PersistedUpload): Promise<void> {
  await tx("readwrite", (store) => store.put(record));
}

/** Retrieves a target's persisted state, or null if there is none. */
export async function loadPersistedUpload(resumeKey: string): Promise<PersistedUpload | null> {
  const result = await tx("readonly", (store) => store.get(resumeKey));
  // Trust-boundary read of the IndexedDB record; its shape is what we wrote.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return (result as PersistedUpload | null) ?? null;
}

/** Deletes the persisted state (upload completed or aborted). */
export async function clearPersistedUpload(resumeKey: string): Promise<void> {
  await tx("readwrite", (store) => store.delete(resumeKey));
}

/** Derives the machine's `ResumeState` from the persisted record. */
export function toResumeState(record: PersistedUpload): ResumeState {
  return {
    assetId: record.assetId,
    uploadId: record.uploadId,
    uploadedParts: record.uploadedParts,
  };
}

/** Stable resume key for a given file/target. */
export function resumeKeyFor(params: {
  courseId?: string | null;
  lessonId?: string | null;
  classSessionId?: string | null;
  fileName: string;
  sizeBytes: number;
}): string {
  const scope = params.classSessionId ?? params.lessonId ?? params.courseId ?? "std";
  return `${scope}:${params.fileName}:${params.sizeBytes}`;
}
